import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, Scale, Boxes, ShieldAlert, Calculator } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

export const useTarifas = () => {
  const [t, setT] = useState(null);
  useEffect(() => { axios.get(`${API}/api/tarifas`).then((r) => setT(r.data)).catch(() => {}); }, []);
  return t;
};

export const TarifasCalculadora = () => {
  const [peso, setPeso] = useState('');
  const [unidad, setUnidad] = useState('g');
  const [permiso, setPermiso] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const valor = parseFloat(String(peso).replace(',', '.'));
    if (!valor || valor <= 0) { setRes(null); setError(''); return; }
    const t = setTimeout(() => {
      axios.post(`${API}/api/tarifas/cotizar`, { peso: valor, unidad, permiso_especial: permiso })
        .then((r) => { setRes(r.data); setError(''); })
        .catch((e) => { setRes(null); setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Revisa el peso ingresado'); });
    }, 250);
    return () => clearTimeout(t);
  }, [peso, unidad, permiso]);

  return (
    <div className="rounded-2xl p-6 md:p-8 text-white" style={{ background: 'var(--sendhapy-teal)' }} data-testid="calculadora-tarifas">
      <h3 className="text-2xl font-bold mb-1 flex items-center gap-2"><Calculator style={{ color: 'var(--sendhapy-yellow)' }} /> Calcula tu envío</h3>
      <p className="text-white/70 text-sm mb-6">Escribe el peso de tu paquete y te mostramos el precio al instante.</p>
      <div className="flex gap-2 mb-4">
        <input data-testid="calc-peso-input" type="number" inputMode="decimal" min="0" step="any" placeholder="Ej: 1350" value={peso}
          onChange={(e) => setPeso(e.target.value)} className="flex-1 min-w-0 px-4 py-3 rounded-xl text-gray-900 text-lg outline-none focus:ring-4 focus:ring-yellow-300" />
        <div className="flex rounded-xl overflow-hidden bg-white/10">
          {[['g', 'gramos'], ['kg', 'kg'], ['lb', 'lb']].map(([u, l]) => (
            <button key={u} type="button" data-testid={`calc-unidad-${u}`} onClick={() => setUnidad(u)}
              className={`px-3 md:px-4 text-sm font-semibold transition-colors ${unidad === u ? 'bg-[#FFE20A] text-[#194D3D]' : 'text-white hover:bg-white/10'}`}>{l}</button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-3 text-sm mb-6 cursor-pointer">
        <input type="checkbox" data-testid="calc-permiso-checkbox" checked={permiso} onChange={(e) => setPermiso(e.target.checked)} className="w-5 h-5 accent-yellow-400" />
        Mi producto requiere permiso especial (cosméticos, perfumes, suplementos, etc.)
      </label>
      <div className="bg-white/10 rounded-xl p-5 min-h-[120px]" data-testid="calc-resultado">
        {res ? (
          <div className="space-y-1">
            <p className="flex justify-between text-sm"><span className="text-white/70">Peso facturable</span><b data-testid="calc-peso-facturable">{res.peso_facturable_kg} kg</b></p>
            <p className="flex justify-between text-sm"><span className="text-white/70">Envío</span><span>{usd(res.precio_base_usd)}</span></p>
            {res.recargo_permiso_usd > 0 && <p className="flex justify-between text-sm"><span className="text-white/70">Permiso especial</span><span data-testid="calc-recargo">+{usd(res.recargo_permiso_usd)}</span></p>}
            <p className="flex justify-between items-end pt-2 border-t border-white/20 mt-2"><span>Total estimado</span><span className="text-4xl font-extrabold" style={{ color: 'var(--sendhapy-yellow)' }} data-testid="calc-total">{usd(res.total_usd)}</span></p>
          </div>
        ) : <p className="text-white/60 text-sm" data-testid="calc-placeholder">{error || 'Ingresa un peso para ver el precio.'}</p>}
      </div>
      <p className="text-xs text-white/60 mt-4">El monto final depende del peso facturable confirmado al recibir tu paquete y de los requisitos que aplique el producto.</p>
    </div>
  );
};

const PricingSection = () => {
  const t = useTarifas();
  const v = (k, d) => (t ? t[k] : d);
  const cards = [
    { icon: Package, title: 'Paquetes pequeños', price: usd(v('precio_hasta_500g_usd', 7)), note: 'hasta 500 g', id: 'pequenos' },
    { icon: Scale, title: 'Tarifa regular', price: `${usd(v('tarifa_kg_usd', 14))}/kg`, note: 'por kilogramo facturable', id: 'regular' },
    { icon: Boxes, title: 'Consolidación', price: 'Gratis', note: 'unimos tus compras sin costo', id: 'consolidacion' },
    { icon: ShieldAlert, title: 'Permiso especial', price: `+${usd(v('recargo_permiso_usd', 2))}`, note: 'por paquete, cuando aplique', id: 'permiso' },
  ];
  const tabla = [
    ['Hasta 500 g', v('precio_hasta_500g_usd', 7)], ['600 g', v('precio_600g_usd', 8.4)], ['700 g', v('precio_700g_usd', 9.8)],
    ['800 g a 1 kg', v('precio_800g_1kg_usd', 14)],
  ];
  return (
    <section id="tarifas" className="py-20 px-6" style={{ background: 'var(--sendhapy-smoke)' }} data-testid="pricing-section">
      <div className="max-w-6xl mx-auto">
        <p className="uppercase tracking-[0.25em] text-xs font-bold mb-3" style={{ color: 'var(--sendhapy-teal)' }}>Tarifas</p>
        <h2 className="text-4xl sm:text-5xl font-extrabold mb-4" style={{ color: 'var(--sendhapy-night)' }}>Precios claros, sin sorpresas</h2>
        <p className="text-base md:text-lg text-gray-600 mb-12 max-w-2xl">Pagas según el peso de tu paquete. Así de simple.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {cards.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl p-6 border border-black/5 transition-transform hover:-translate-y-1" data-testid={`tarifa-card-${c.id}`}>
              <c.icon size={28} style={{ color: 'var(--sendhapy-teal)' }} className="mb-4" />
              <p className="text-sm text-gray-500">{c.title}</p>
              <p className="text-3xl font-extrabold my-1" style={{ color: 'var(--sendhapy-teal)' }}>{c.price}</p>
              <p className="text-sm text-gray-600">{c.note}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start [&>*]:min-w-0">
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-black/5">
            <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--sendhapy-teal)' }}>¿Cómo se calcula el peso?</h3>
            <table className="w-full text-sm mb-6" data-testid="tabla-tarifas">
              <tbody>
                {tabla.map(([p, precio]) => (
                  <tr key={p} className="border-b last:border-0"><td className="py-2.5 text-gray-600">{p}</td><td className="py-2.5 text-right font-bold">{usd(precio)}</td></tr>
                ))}
                <tr><td className="py-2.5 text-gray-600">Más de 1 kg</td><td className="py-2.5 text-right font-bold">{usd(v('tarifa_kg_usd', 14))} por kg facturable</td></tr>
              </tbody>
            </table>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• El peso se redondea hacia arriba en tramos de 100 g.</li>
              <li>• Sobre 1 kg: 1.3 y 1.4 kg se cobran como 1.5 kg; 1.8 y 1.9 kg se cobran como 2 kg (igual en cada kilo).</li>
              <li>• Los demás pesos se cobran tal cual: por ejemplo, 1.2 kg = {usd(1.2 * v('tarifa_kg_usd', 14))}.</li>
              <li>• Productos con permiso especial: +{usd(v('recargo_permiso_usd', 2))} por paquete.</li>
            </ul>
          </div>
          <TarifasCalculadora />
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
